"use client";

/** One open-list for an accordion and its expand/collapse control, which sit
 *  apart in the markup (the control is in the section header). The itinerary
 *  and each FAQ panel use one group each.
 *
 *  One control, not two: "Collapse all" on an already-collapsed list did
 *  nothing and still asked to be read, so the offer is whichever move is
 *  available, matching the operator builder's itinerary list. */

import { createContext, useContext, useState, type ReactNode } from "react";
import { Accordion } from "@tripcart/traveler-ui/components/accordion";
import { Button } from "@tripcart/traveler-ui/components/button";

import { useTranslator } from "@/lib/translation-context";

interface Group {
  open: string[];
  setOpen: (open: string[]) => void;
  values: string[];
}

const GroupContext = createContext<Group | null>(null);

function useGroup(): Group {
  const group = useContext(GroupContext);
  if (!group) {
    throw new Error(
      "GroupAccordion and AccordionControls must be inside an <AccordionGroup>.",
    );
  }
  return group;
}

export function AccordionGroup({
  values,
  defaultOpen,
  children,
}: {
  /** Every item value in the group, in order. */
  values: string[];
  defaultOpen: string[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <GroupContext.Provider value={{ open, setOpen, values }}>
      {children}
    </GroupContext.Provider>
  );
}

export function GroupAccordion({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const { open, setOpen } = useGroup();
  return (
    <Accordion
      multiple
      value={open}
      onValueChange={setOpen}
      className={className}
    >
      {children}
    </Accordion>
  );
}

export function AccordionControls({
  className,
  label,
}: {
  className?: string;
  /** The group's name. Several groups on one page (FAQ categories) each get a
   *  control, and this tells their accessible names apart. */
  label?: string;
}) {
  const t = useTranslator();
  const { open, setOpen, values } = useGroup();
  const allOpen = values.every((value) => open.includes(value));
  const ariaLabel = label
    ? allOpen
      ? t("Collapse all in {group}", { group: label })
      : t("Expand all in {group}", { group: label })
    : undefined;
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => setOpen(allOpen ? [] : values)}
      aria-label={ariaLabel}
      className={className}
    >
      {allOpen ? t("Collapse all") : t("Expand all")}
    </Button>
  );
}
